import {
  AFPTable,
  AFPTableRowAction,
  Button,
  FilterTableFrame,
  Pagination,
  StatusBadge,
  Tag,
  EmptyState,
} from '@gsa/afp-component-library';
// import OverlaySpinner from '../../components/overlay-spinner';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppAbility, Can } from '@gsa/afp-shared-ui-utils';
import Truncate from '../../components/Truncate';
import useDidMountEffect from '../../hooks/use-did-mount';
import { SI_LABELS } from './constants';
import StandardItemModalWrapper from './standard-item-modal';
import { useStandards } from './standards-provider';
import './styles/standard-items-table.scss';
import { OPERATIONS, SUBJECTS } from '../../utilities/constants';
import StandardItemFilters from './standard-item-filter-panel';
import StandardItemAddButton from './standard-item-add-button';

/* eslint-disable react/prop-types */
const TableWrapper = ({ tableProps, paginationProps }) => {
  // const { sinDataFetchLoading: isLoading } = useStandards();
  return (
    <>
      <AFPTable {...tableProps} />
      {/* temporary removing spinner on this page, due to stale data */}
      {/* {isLoading && (
        <div className="grid-row margin-top-2 text-center">
          <OverlaySpinner />
        </div>
      )} */}

      {tableProps.data?.length > 0 && (
        <Pagination {...paginationProps} />
      )}

      {!tableProps.data?.length && (
        <EmptyState
          hasBackground
          containerStyles="padding-top-9 height-full text-center"
          topText={<strong>No Standard Item/s with the applied filter.</strong>}
        />
      )}
    </>
  );
};

const FTF = FilterTableFrame(null, null, StandardItemFilters, TableWrapper);

const defaultSort = [['standardItemNumber', 'ASC']];

const actions = [
  {
    icon: 'edit',
    label: SI_LABELS.EDIT,
    canShowIndicator: 'canEdit',
  },
  {
    icon: 'visibility',
    label: SI_LABELS.VIEW,
    canShowIndicator: 'canView',
  },
  {
    icon: 'settings',
    label: SI_LABELS.MANAGE,
    canShowIndicator: 'canEdit',
  },
];

const StandardItemsTable = () => {
  const { setData, standardItemList, getStandardItems } = useStandards();
  const ability = useAppAbility();
  const defaultFilters = {
    operator: 'AND',
    value: [
      {
        key: 'year',
        operator: 'EQ',
        value: `${new Date().getFullYear() + 1}`,
      },
    ],
  };

  const [filters, setFilters] = useState(defaultFilters);
  const [order, setOrder] = useState(defaultSort);
  const [paginationState, setPaginationState] = useState({
    limit: 50,
    offset: 0,
    currentPage: 1,
  });

  const navigate = useNavigate();

  const tableRef = useRef(null);

  const getData = () => {
    const { limit, offset } = paginationState;
    const variables = { filters, limit, offset, order };
    getStandardItems({ variables });
  };

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    const offset = (currentPage - 1) * itemsPerPage;
    setPaginationState({
      limit: itemsPerPage,
      offset,
      currentPage,
    });
  };

  useDidMountEffect(() => {
    handlePaginationChange(1, paginationState.limit);
    setOrder(defaultSort);
    tableRef.current.toggleSortBy('standardItemNumber', false);
  }, [filters]);

  useDidMountEffect(() => {
    getData();
  }, [order, paginationState]);

  const handleSelectedAction = (evt, row) => {
    // SET SELECTED ITEM.
    setData('SET_SELECTED_ITEM', row.original);

    if (evt === SI_LABELS.EDIT) {
      setData('SET_MODAL_MODE', 'edit');
      setData('SET_FORM_MODAL', true);
    }
    if (evt === SI_LABELS.MANAGE || evt === SI_LABELS.VIEW) {
      const { standardItemNumber, year } = row.original;
      const url = `/catalog/standard-item/${standardItemNumber}-${year}`;
      navigate(url, { state: row.original });
    }
    if (evt === 'view_associations') {
      setData('SET_MODAL_MODE', 'view_associations');
      setData('SET_FORM_MODAL', true);
    }
    if (evt === SI_LABELS.DELETE) {
      setData('SET_MODAL_MODE', 'confirm_delete');
      setData('SET_FORM_MODAL', true);
    }
  };

  const columns = useMemo(() => {
    const columnList = [
      {
        Header: SI_LABELS.NUMBER,
        accessor: 'standardItemNumber',
        Cell: ({ row, value }) => (
          <Button
            variant="unstyled"
            onClick={() => handleSelectedAction(SI_LABELS.MANAGE, row)}
            label={value}
          />
        ),
      },
      {
        Header: SI_LABELS.FED_CODE,
        accessor: 'fedStandard.code',
      },
      {
        Header: SI_LABELS.TYPE,
        accessor: 'vehicleTypeCode.title',
      },
      {
        Header: SI_LABELS.YEAR,
        accessor: 'year',
      },
      {
        Header: SI_LABELS.STATUS,
        accessor: 'isActive',
        Cell: ({ value }) => (
          <StatusBadge variant={value === 1 ? 'Ready-Gray' : 'Inactive-Gray'}>
            {value === 1 ? 'Active' : 'Inactive'}
          </StatusBadge>
        ),
      },
      {
        Header: SI_LABELS.IS_ARCHIVED,
        sortable: false,
        accessor: 'status',
        Cell: ({ row }) => {
          const sinYear = row?.original?.year;
          const currentYear = new Date().getFullYear();
          return sinYear <= currentYear ? 'Yes' : 'No';
        },
      },
      {
        Header: SI_LABELS.TITLE,
        accessor: 'title',
        cellClassName: 'afpTable-cell-wordBreak',
      },
      {
        Header: SI_LABELS.ASSOCIATED_VIEW,
        sortable: false,
        Cell: (props) => (
          <Button
            variant="unstyled"
            onClick={() => handleSelectedAction('view_associations', props.row)}
            label="View"
          />
        ),
      },
    ];

    if (ability.can(OPERATIONS.Manage, SUBJECTS.StandardItem)) {
      columnList.push({
        Header: 'Actions',
        sortable: false,
        Cell: (props) => (
          <AFPTableRowAction
            {...props}
            actions={actions}
            onSelectAction={(evt) => handleSelectedAction(evt, props.row)}
          />
        ),
      });
    }

    return columnList;
  }, [ability]);

  const renderRowSubComponent = useCallback(({ row: { original } }) => {
    return (
      <div className="grid-container standard-items-sub-component-wrapper">
        <div className="grid-row grid-gap">
          <div className="tablet:grid-col">
            <div className="grid-row standard-item-wrapper standard-item-separator">
              <div className="tablet:grid-col standard-item-title">
                Federal standard code
              </div>
              <div className="tablet:grid-col  text-right">
                {original?.fedStandard?.code ? (
                  <div>
                    {original?.fedStandard?.code} (
                    {original?.fedStandard?.title})
                  </div>
                ) : (
                  <div className="text-bold">&ndash;</div>
                )}
              </div>
            </div>

            <div className="grid-row standard-item-wrapper standard-item-separator">
              <div className="tablet:grid-col standard-item-title">Tags</div>
              <div className="tablet:grid-col text-right standard-item-last-row">
                {original?.tags?.value?.length ? (
                  [...original?.tags?.value].map((tag) => (
                    <Tag
                      key={tag}
                      className="usa-tag afp-tag bg-base text-white standard-item-tag-item"
                    >
                      {tag}
                    </Tag>
                  ))
                ) : (
                  <div className="text-bold">&ndash;</div>
                )}
              </div>
            </div>

            <div className="grid-row standard-item-wrapper standard-item-separator">
              <div className="tablet:grid-col standard-item-title">
                FAST Code
              </div>
              <div className="tablet:grid-col  text-right">
                {original?.fastCode?.title ? (
                  <div>{original?.fastCode?.title}</div>
                ) : (
                  <div className="text-bold">&ndash;</div>
                )}
              </div>
            </div>
          </div>
          <div className="tablet:grid-col">
            <div className="standard-item-wrapper">
              <div className="standard-item-title">Description</div>

              {original?.content?.content ? (
                <Truncate content={original?.content?.content} size={100} />
              ) : (
                <div className="text-bold">
                  <br />
                  &ndash;
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }, []);

  return (
    <>
      <Can I={OPERATIONS.Manage} a={SUBJECTS.StandardItem}>
        <StandardItemAddButton setData={setData} />
      </Can>

      <FTF
        lowLeftProps={{
          currentFilters: filters,
          setFilters,
        }}
        lowRightProps={{
          tableProps: {
            expandable: true,
            columns,
            data: standardItemList.rows || [],
            renderRowSubComponent,
            onSort: setOrder,
            defaultSort: order,
            fullWidth: true,
            ref: tableRef,
            testId: 'standard-items-table',
            hiddenColumns: ['fedStandard.code'],
          },
          paginationProps: {
            itemsPerPageOptions: [50, 100, 150, 200],
            onPageChange: handlePaginationChange,
            variant: 'advanced',
            itemsPerPage: paginationState.limit,
            currentPage: paginationState.currentPage,
            itemsCount: standardItemList?.count,
          },
        }}
        filterToggle
      />
      <StandardItemModalWrapper />
    </>
  );
};

export default StandardItemsTable;
